// ===========================================
// DASHBOARD SERVICE - Production Ready
// ===========================================

import api from './api';

export interface DashboardStats {
  overview: {
    totalRevenue: number;
    revenueChange: number;
    totalBookings: number;
    bookingsChange: number;
    totalLeads: number;
    leadsChange: number;
    conversionRate: number;
    conversionChange: number;
  };
  period: string;
  comparisonPeriod: string;
}

export interface RevenueData {
  total: number;
  byPeriod: {
    date: string;
    revenue: number;
    bookings: number;
    profit: number;
  }[];
  byCategory: {
    category: string;
    revenue: number;
    percentage: number;
  }[];
  byDestination: {
    destination: string;
    revenue: number;
    bookings: number;
  }[];
  forecast: {
    date: string;
    predicted: number;
    lower: number;
    upper: number;
  }[];
}

export interface LeadFunnel {
  stages: {
    name: string;
    count: number;
    value: number;
    conversionRate: number;
    avgTimeInStage: number;
  }[];
  velocity: number;
  projectedRevenue: number;
}

export interface TeamPerformance {
  members: {
    id: string;
    name: string;
    avatar?: string;
    role: string;
    metrics: {
      bookings: number;
      revenue: number;
      leads: number;
      conversionRate: number;
      avgDealSize: number;
      tasksCompleted: number;
    };
    trend: 'UP' | 'DOWN' | 'STABLE';
    ranking: number;
  }[];
  totals: {
    bookings: number;
    revenue: number;
    leads: number;
    conversionRate: number;
  };
}

export interface ActivityFeed {
  activities: {
    id: string;
    type: string;
    action: string;
    description: string;
    entityType?: string;
    entityId?: string;
    entityName?: string;
    user: {
      id: string;
      name: string;
      avatar?: string;
    };
    timestamp: string;
    metadata?: Record<string, unknown>;
  }[];
  hasMore: boolean;
}

export interface TasksSummary {
  total: number;
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
  byPriority: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  byStatus: {
    PENDING: number;
    IN_PROGRESS: number;
    COMPLETED: number;
  };
  completionRate: number;
  upcomingTasks: {
    id: string;
    title: string;
    dueDate: string;
    priority: string;
    assignee?: { id: string; name: string };
    relatedTo?: { type: string; id: string; name: string };
  }[];
}

export interface UpcomingBookings {
  bookings: {
    id: string;
    bookingNumber: string;
    client: {
      id: string;
      name: string;
      avatar?: string;
    };
    destination: string;
    departureDate: string;
    returnDate: string;
    totalAmount: number;
    status: string;
    paidAmount: number;
    balanceDue: number;
  }[];
  total: number;
  totalValue: number;
}

export interface CampaignPerformance {
  campaigns: {
    id: string;
    name: string;
    type: string;
    status: string;
    sent: number;
    opened: number;
    clicked: number;
    converted: number;
    revenue: number;
    openRate: number;
    clickRate: number;
    conversionRate: number;
    roi: number;
  }[];
  totals: {
    sent: number;
    opened: number;
    clicked: number;
    converted: number;
    revenue: number;
  };
}

export const dashboardService = {
  // Overview Stats
  async getStats(period: '7d' | '30d' | '90d' | 'year' = '30d') {
    return api.get<DashboardStats>(`/api/dashboard/stats?period=${period}`);
  },

  async getQuickStats() {
    return api.get<{
      todayBookings: number;
      todayRevenue: number;
      pendingTasks: number;
      unreadMessages: number;
      newLeads: number;
      overdueInvoices: number;
    }>('/api/dashboard/quick-stats');
  },

  // Revenue Analytics
  async getRevenueData(period: '7d' | '30d' | '90d' | 'year' = '30d') {
    return api.get<RevenueData>(`/api/dashboard/revenue?period=${period}`);
  },

  async getRevenueComparison(currentPeriod: string, comparisonPeriod: string) {
    return api.get<{
      current: { total: number; bookings: number; profit: number };
      comparison: { total: number; bookings: number; profit: number };
      change: { total: number; bookings: number; profit: number };
    }>(`/api/dashboard/revenue/compare?current=${currentPeriod}&comparison=${comparisonPeriod}`);
  },

  // Lead Funnel
  async getLeadFunnel() {
    return api.get<LeadFunnel>('/api/dashboard/funnel');
  },

  async getLeadSources() {
    return api.get<{
      sources: {
        source: string;
        count: number;
        converted: number;
        conversionRate: number;
        revenue: number;
      }[];
    }>('/api/dashboard/leads/sources');
  },

  async getLeadTrends(period: '7d' | '30d' | '90d' = '30d') {
    return api.get<{
      byDay: { date: string; new: number; converted: number; lost: number }[];
      totals: { new: number; converted: number; lost: number };
    }>(`/api/dashboard/leads/trends?period=${period}`);
  },

  // Team Performance
  async getTeamPerformance(period: '7d' | '30d' | '90d' = '30d') {
    return api.get<TeamPerformance>(`/api/dashboard/team?period=${period}`);
  },

  async getUserPerformance(userId: string, period: '7d' | '30d' | '90d' = '30d') {
    return api.get<{
      user: { id: string; name: string; avatar?: string };
      metrics: {
        bookings: number;
        revenue: number;
        leads: number;
        conversionRate: number;
        avgDealSize: number;
        tasksCompleted: number;
      };
      byDay: { date: string; bookings: number; revenue: number }[];
      comparison: {
        teamAvg: Record<string, number>;
        percentile: number;
      };
    }>(`/api/dashboard/team/${userId}?period=${period}`);
  },

  // Activity Feed
  async getActivityFeed(page = 1, limit = 20, filters?: {
    type?: string;
    userId?: string;
  }) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.type) params.append('type', filters.type);
    if (filters?.userId) params.append('userId', filters.userId);
    
    return api.get<ActivityFeed>(`/api/dashboard/activity?${params.toString()}`);
  },

  // Tasks
  async getTasksSummary() {
    return api.get<TasksSummary>('/api/dashboard/tasks');
  },

  async getMyTasks(status?: string, limit = 10) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.append('status', status);
    
    return api.get<{
      tasks: {
        id: string;
        title: string;
        description?: string;
        dueDate: string;
        priority: string;
        status: string;
        relatedTo?: { type: string; id: string; name: string };
      }[];
      total: number;
    }>(`/api/dashboard/my-tasks?${params.toString()}`);
  },

  // Upcoming Bookings
  async getUpcomingBookings(days = 30, limit = 10) {
    return api.get<UpcomingBookings>(`/api/dashboard/bookings/upcoming?days=${days}&limit=${limit}`);
  },

  async getBookingsByStatus() {
    return api.get<{
      byStatus: {
        status: string;
        count: number;
        value: number;
      }[];
      total: number;
      totalValue: number;
    }>('/api/dashboard/bookings/by-status');
  },

  // Campaign Performance
  async getCampaignPerformance(period: '7d' | '30d' | '90d' = '30d') {
    return api.get<CampaignPerformance>(`/api/dashboard/campaigns?period=${period}`);
  },

  // Invoices & Payments
  async getInvoicesSummary() {
    return api.get<{
      outstanding: number;
      overdue: number;
      paidThisMonth: number;
      pendingCount: number;
      overdueCount: number;
      recentPayments: {
        id: string;
        amount: number;
        client: string;
        date: string;
      }[];
    }>('/api/dashboard/invoices');
  },

  // Goals & Targets
  async getGoalsProgress() {
    return api.get<{
      goals: {
        id: string;
        name: string;
        target: number;
        current: number;
        progress: number;
        unit: string;
        deadline?: string;
        status: 'ON_TRACK' | 'AT_RISK' | 'BEHIND' | 'COMPLETED';
      }[];
    }>('/api/dashboard/goals');
  },

  async setGoal(data: {
    name: string;
    target: number;
    unit: string;
    deadline?: string;
    type: string;
  }) {
    return api.post('/api/dashboard/goals', data);
  },

  // Notifications
  async getNotifications(unreadOnly = false, limit = 20) {
    return api.get<{
      notifications: {
        id: string;
        type: string;
        title: string;
        message: string;
        read: boolean;
        actionUrl?: string;
        createdAt: string;
      }[];
      unreadCount: number;
    }>(`/api/dashboard/notifications?unreadOnly=${unreadOnly}&limit=${limit}`);
  },

  async markNotificationRead(id: string) {
    return api.post(`/api/dashboard/notifications/${id}/read`);
  },

  async markAllNotificationsRead() {
    return api.post('/api/dashboard/notifications/read-all');
  },

  // Custom Widgets
  async getWidgetData(widgetId: string, params?: Record<string, string>) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return api.get(`/api/dashboard/widgets/${widgetId}${queryString}`);
  },

  async getWidgetConfig() {
    return api.get<{
      widgets: {
        id: string;
        type: string;
        title: string;
        position: { x: number; y: number; w: number; h: number };
        config?: Record<string, unknown>;
      }[];
    }>('/api/dashboard/widgets/config');
  },

  async saveWidgetConfig(widgets: {
    id: string;
    position: { x: number; y: number; w: number; h: number };
  }[]) {
    return api.post('/api/dashboard/widgets/config', { widgets });
  },

  // Export
  async exportDashboard(format: 'pdf' | 'xlsx' = 'pdf', period = '30d') {
    const response = await fetch(`/api/dashboard/export?format=${format}&period=${period}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    return response.blob();
  },
};

export default dashboardService;
